import React, {
	useState,
	useEffect,
	useReducer,
	useCallback,
	useContext,
	useRef,
	useMemo,
} from "react";
import { toast } from "react-toastify";

import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import ConfirmationModal from "../../components/ConfirmationModal";
import PermissionModal from "../../components/PermissionModal";
import QuickAnswersModal from "../../components/QuickAnswersModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const PAGE_SIZE = 20;

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus({ className = "size-4 me-1 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconChevronLeft({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function QuickAnswerRowMenu({ onEdit, onDelete }) {
	return (
		<AppTableActionMenu
			triggerIcon={<IconMore />}
			triggerAriaLabel={i18n.t("quickAnswers.table.actions")}
			items={[
				{
					key: "edit",
					label: i18n.t("quickAnswers.buttons.edit"),
					icon: <IconEdit />,
					onClick: onEdit,
				},
				{
					key: "delete",
					label: i18n.t("quickAnswers.buttons.delete"),
					icon: <IconDelete />,
					tone: "danger",
					onClick: onDelete,
				},
			]}
		/>
	);
}

const reducer = (state, action) => {
	if (action.type === "SET_QUICK_ANSWERS") {
		return action.payload;
	}

	if (action.type === "UPDATE_QUICK_ANSWERS") {
		const quickAnswer = action.payload;
		const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswer.id);

		if (quickAnswerIndex !== -1) {
			state[quickAnswerIndex] = quickAnswer;
			return [...state];
		}
		return [quickAnswer, ...state];
	}

	if (action.type === "DELETE_QUICK_ANSWERS") {
		const quickAnswerId = action.payload;
		const quickAnswerIndex = state.findIndex((q) => q.id === quickAnswerId);
		if (quickAnswerIndex !== -1) {
			state.splice(quickAnswerIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

function QuickAnswers() {
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [total, setTotal] = useState(0);
	const [searchParam, setSearchParam] = useState("");
	const [quickAnswers, dispatch] = useReducer(reducer, []);
	const [selectedQuickAnswers, setSelectedQuickAnswers] = useState(null);
	const [quickAnswersModalOpen, setQuickAnswersModalOpen] = useState(false);
	const [deletingQuickAnswers, setDeletingQuickAnswers] = useState(null);
	const [deletingSelectedIds, setDeletingSelectedIds] = useState(null);
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const { user } = useContext(AuthContext);

	const fetchQuickAnswers = useCallback(async () => {
		try {
			const { data } = await api.get("/quickAnswers/", {
				params: { searchParam, pageNumber },
			});
			dispatch({ type: "SET_QUICK_ANSWERS", payload: data.quickAnswers || [] });
			setTotal(data.count ?? 0);
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	}, [searchParam, pageNumber]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setSelectedIds(new Set());
	}, [searchParam]);

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			fetchQuickAnswers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, pageNumber, fetchQuickAnswers]);

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const rangeStart = total ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, total);

	const pageNumbers = () => {
		const pages = [];
		const max = 5;
		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + max - 1);
		from = Math.max(1, to - max + 1);
		for (let p = from; p <= to; p += 1) pages.push(p);
		return pages;
	};

	const visibleIds = useMemo(() => quickAnswers.map((q) => q.id), [quickAnswers]);
	const allVisibleSelected =
		visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
	const someVisibleSelected =
		visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;
	const selectedCount = selectedIds.size;

	const selectAllRef = useRef(null);
	useEffect(() => {
		if (!selectAllRef.current) return;
		selectAllRef.current.indeterminate = someVisibleSelected;
		selectAllRef.current.checked = allVisibleSelected;
	}, [someVisibleSelected, allVisibleSelected]);

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allVisibleSelected) {
				visibleIds.forEach((id) => next.delete(id));
			} else {
				visibleIds.forEach((id) => next.add(id));
			}
			return next;
		});
	};

	const toggleSelectOne = (id) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	useEffect(() => {
		const socket = openSocket({
			scope: "quickanswers",
			userId: user.id,
			component: "QuickAnswers",
		});

		socket.on("quickAnswer", (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_QUICK_ANSWERS", payload: data.quickAnswer });
			}
			if (data.action === "delete") {
				dispatch({
					type: "DELETE_QUICK_ANSWERS",
					payload: +data.quickAnswerId,
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user?.id]);

	const handleSearch = (event) => {
		setSearchParam(event.target.value);
	};

	const handleOpenQuickAnswersModal = () => {
		setSelectedQuickAnswers(null);
		setQuickAnswersModalOpen(true);
	};

	const handleCloseQuickAnswersModal = () => {
		setSelectedQuickAnswers(null);
		setQuickAnswersModalOpen(false);
	};

	const handleEditQuickAnswers = (quickAnswer) => {
		setSelectedQuickAnswers(quickAnswer);
		setQuickAnswersModalOpen(true);
	};

	const handleDeleteQuickAnswers = async (quickAnswerId) => {
		try {
			await api.delete(`/quickAnswers/${quickAnswerId}`);
			toast.success(i18n.t("quickAnswers.toasts.deleted"));
		} catch (err) {
			toastError(err);
		}
		setDeletingQuickAnswers(null);
		setConfirmModalOpen(false);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.delete(quickAnswerId);
			return next;
		});
		if (quickAnswers.length === 1 && pageNumber > 1) {
			setPageNumber((p) => p - 1);
		} else {
			await fetchQuickAnswers();
		}
	};

	const handleDeleteSelected = async (ids) => {
		try {
			await Promise.all(ids.map((id) => api.delete(`/quickAnswers/${id}`)));
			toast.success(i18n.t("quickAnswers.toasts.deletedSelected"));
		} catch (err) {
			toastError(err);
		}
		setDeletingSelectedIds(null);
		setConfirmModalOpen(false);
		setSelectedIds(new Set());
		if (quickAnswers.length <= ids.length && pageNumber > 1) {
			setPageNumber((p) => p - 1);
		} else {
			await fetchQuickAnswers();
		}
	};

	const confirmDeleteSelected = () => {
		if (selectedCount === 0) return;
		if (user.profile !== "admin") {
			setPermissionOpen(true);
			return;
		}
		setDeletingQuickAnswers(null);
		setDeletingSelectedIds([...selectedIds]);
		setConfirmModalOpen(true);
	};

	const confirmDeleteOne = (quickAnswer) => {
		if (user.profile !== "admin") {
			setPermissionOpen(true);
			return;
		}
		setDeletingQuickAnswers(quickAnswer);
		setDeletingSelectedIds(null);
		setConfirmModalOpen(true);
	};

	return (
		<div className="app-admin-page">
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			<ConfirmationModal
				title={
					deletingQuickAnswers
						? `${i18n.t("quickAnswers.confirmationModal.deleteTitle")} ${deletingQuickAnswers.shortcut}?`
						: i18n.t("quickAnswers.confirmationModal.deleteSelectedTitle")
				}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={() =>
					deletingQuickAnswers
						? handleDeleteQuickAnswers(deletingQuickAnswers.id)
						: handleDeleteSelected(deletingSelectedIds)
				}
			>
				{deletingQuickAnswers
					? i18n.t("quickAnswers.confirmationModal.deleteMessage")
					: i18n.t("quickAnswers.confirmationModal.deleteSelectedMessage", {
							count: deletingSelectedIds?.length ?? 0,
					  })}
			</ConfirmationModal>
			<QuickAnswersModal
				open={quickAnswersModalOpen}
				onClose={handleCloseQuickAnswersModal}
				quickAnswerId={selectedQuickAnswers?.id}
				reload={fetchQuickAnswers}
			/>

			<AppPageTitle
				title={i18n.t("quickAnswers.title")}
				subtitle={i18n.t("quickAnswers.subtitle")}
			/>

			<div className="card">
				<div className="card-header">
					<h6 className="card-title m-0">{i18n.t("quickAnswers.listTitle")}</h6>
					<button
						type="button"
						className="btn btn-sm shrink-0 bg-primary text-white"
						onClick={handleOpenQuickAnswersModal}
					>
						<IconPlus />
						{i18n.t("quickAnswers.buttons.add")}
					</button>
				</div>

				<div className="card-header flex-wrap gap-3">
					<div className="relative w-full md:max-w-sm">
						<input
							type="search"
							className="form-input form-input-sm w-full ps-9"
							placeholder={i18n.t("quickAnswers.searchPlaceholder")}
							value={searchParam}
							onChange={handleSearch}
						/>
						<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
							<IconSearch />
						</div>
					</div>

					{selectedCount > 0 && (
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-sm text-default-500 dark:text-default-400">
								{i18n.t("quickAnswers.selectedCount", { count: selectedCount })}
							</span>
							<button
								type="button"
								className="btn btn-sm bg-danger/10 text-danger hover:bg-danger/20 dark:bg-danger/15 dark:hover:bg-danger/25"
								onClick={confirmDeleteSelected}
							>
								<IconTrash />
								<span className="ms-1">{i18n.t("quickAnswers.buttons.deleteSelected")}</span>
							</button>
						</div>
					)}
				</div>

				<div className="flex min-h-0 flex-1 flex-col">
					{loading && quickAnswers.length === 0 ? (
						<div className="overflow-x-auto px-2 pt-2">
							<table className="min-w-full">
								<tbody>
									<TableRowSkeleton columns={5} />
								</tbody>
							</table>
						</div>
					) : !loading && quickAnswers.length === 0 ? (
						<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
							<p className="m-0 font-semibold text-default-800 dark:text-default-100">
								{i18n.t("quickAnswers.empty")}
							</p>
							{searchParam && (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									{i18n.t("quickAnswers.emptyHint")}
								</p>
							)}
						</div>
					) : (
						<div className="flex min-h-0 flex-1 flex-col">
							<div className="app-table-scroll">
								<div className="inline-block min-w-full align-middle">
									<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
										<thead className="bg-default-150 dark:bg-default-800/60">
											<tr className="text-sm font-normal text-default-700 dark:text-default-300">
												<th scope="col" className="ps-4 text-start">
													<input
														ref={selectAllRef}
														type="checkbox"
														className="form-checkbox"
														checked={allVisibleSelected}
														onChange={toggleSelectAll}
														aria-label={i18n.t("quickAnswers.selectAll")}
														disabled={visibleIds.length === 0 || loading}
													/>
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("quickAnswers.table.shortcut")}
												</th>
												<th className="px-3.5 py-3 text-start">
													{i18n.t("quickAnswers.table.message")}
												</th>
												<th className="px-3.5 py-3 text-center">
													{i18n.t("quickAnswers.table.file")}
												</th>
												<th className="px-3.5 py-3 text-center">
													{i18n.t("quickAnswers.table.actions")}
												</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-default-200 dark:divide-default-700">
											{quickAnswers.map((quickAnswer) => (
												<tr
													key={quickAnswer.id}
													className="text-sm text-default-800 dark:text-default-200"
												>
													<td className="py-3 ps-4">
														<input
															type="checkbox"
															className="form-checkbox"
															checked={selectedIds.has(quickAnswer.id)}
															onChange={() => toggleSelectOne(quickAnswer.id)}
															aria-label={i18n.t("quickAnswers.selectRow", {
																shortcut: quickAnswer.shortcut,
															})}
														/>
													</td>
													<td className="whitespace-nowrap px-3.5 py-2.5 font-medium">
														{quickAnswer.shortcut}
													</td>
													<td
														className="max-w-md truncate px-3.5 py-2.5"
														title={quickAnswer.message}
													>
														{quickAnswer.message}
													</td>
													<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
														{quickAnswer.file ? (
															<span className="inline-flex rounded px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary">
																{i18n.t("quickAnswers.table.hasFile")}
															</span>
														) : (
															<span className="text-default-400">—</span>
														)}
													</td>
													<td className="whitespace-nowrap px-3.5 py-2.5 text-center">
														<QuickAnswerRowMenu
															onEdit={() => handleEditQuickAnswers(quickAnswer)}
															onDelete={() => confirmDeleteOne(quickAnswer)}
														/>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							{total > 0 && (
								<div className="card-footer">
									<p className="m-0 text-sm text-default-500">
										{i18n.t("quickAnswers.pagination.showing", {
											start: rangeStart,
											end: rangeEnd,
											total,
										})}
									</p>
									<nav
										className="flex flex-wrap items-center gap-2"
										aria-label={i18n.t("quickAnswers.pagination.label")}
									>
										<button
											type="button"
											className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
											disabled={pageNumber <= 1 || loading}
											onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
										>
											<IconChevronLeft className="size-4 me-1 shrink-0" />
											{i18n.t("quickAnswers.pagination.previous")}
										</button>
										{pageNumbers().map((p) => (
											<button
												key={p}
												type="button"
												className={`btn size-7.5 shrink-0 border ${
													p === pageNumber
														? "border-primary bg-primary text-white"
														: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
												}`}
												disabled={loading}
												onClick={() => setPageNumber(p)}
											>
												{p}
											</button>
										))}
										<button
											type="button"
											className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
											disabled={pageNumber >= totalPages || loading}
											onClick={() => setPageNumber((p) => p + 1)}
										>
											{i18n.t("quickAnswers.pagination.next")}
											<IconChevronRight className="size-4 ms-1 shrink-0" />
										</button>
									</nav>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default QuickAnswers;
